import { useCallback, useContext } from 'react';
import { MarkdownContext, RenderEngine } from '../index';

// 创建 useMarkdown hook
export const useMarkdown = () => {
  const { 
    markdown, 
    setMarkdown, 
    renderedMarkdown, 
    setRenderedMarkdown,
    renderEngine,
    setRenderEngine
  } = useContext(MarkdownContext);

  // 转换公式格式
  const convertFormulas = useCallback((text: string) => {
    if (!text) return '';
    
    try {
      let result = text;
      
      // 先处理LaTeX块级公式 \[ ... \] 为 $$ ... $$
      // 使用修改的正则表达式匹配多行内容
      result = result.replace(/\\\[([\s\S]*?)\\\]/g, (match, p1) => {
        return `$$${p1.trim()}$$`;
      });
      
      // 再处理LaTeX行内公式 \( ... \) 为 $ ... $
      result = result.replace(/\\\(([\s\S]*?)\\\)/g, (match, p1) => {
        return `$${p1.trim()}$`;
      });
      
      // 对百分号进行特殊处理：根据渲染引擎的不同，处理方式会有所不同
      if (renderEngine === 'katex') {
        // KaTeX需要对百分号进行转义，将单个%转为\\%
        result = result.replace(/\$([^$]*?[^\\]%[^$]*?)\$/g, (match, p1) => {
          return `$${p1.replace(/%/g, '\\%')}$`;
        });
        result = result.replace(/\$\$([^$]*?[^\\]%[^$]*?)\$\$/g, (match, p1) => {
          return `$$${p1.replace(/%/g, '\\%')}$$`;
        });
      }
      
      // 清理可能存在的代码块标记问题
      // 去除错误的代码块格式化
      result = result.replace(/```\$\$([\s\S]*?)\$\$```/g, '$$$$1$$');
      
      console.log("转换前:", text.substring(0, 50));
      console.log("转换后:", result.substring(0, 50));
      console.log("使用渲染引擎:", renderEngine);
      
      return result;
    } catch (error) {
      console.error("公式转换错误:", error);
      return text; // 发生错误时返回原始文本
    }
  }, [renderEngine]);

  // 从文件加载 Markdown
  const loadMarkdownFromFile = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      // 使用类型守卫而非类型断言
      const content = typeof e.target?.result === 'string' ? e.target.result : '';
      if (content) {
        setMarkdown(content);
        // 不立即设置渲染内容，等待用户点击转换按钮
        setRenderedMarkdown('');
      }
    };
    reader.readAsText(file);
  }, [setMarkdown, setRenderedMarkdown]);

  // 处理渲染
  const convertAndRender = useCallback(() => {
    // 转换公式并更新渲染内容
    const converted = convertFormulas(markdown);
    setRenderedMarkdown(converted);
    console.log("已渲染内容长度:", converted.length); 
    console.log("已渲染内容片段:", converted.substring(0, 200)); 
    return converted;
  }, [markdown, convertFormulas, setRenderedMarkdown]);

  // 切换渲染引擎
  const switchRenderEngine = useCallback((engine: RenderEngine) => {
    setRenderEngine(engine);
    // 如果已经有渲染内容，则重新渲染
    if (renderedMarkdown) {
      // 需要重新转换，因为不同渲染引擎对公式的转义要求不同
      setTimeout(() => convertAndRender(), 0);
    }
  }, [setRenderEngine, renderedMarkdown, convertAndRender]);

  return {
    markdown,
    setMarkdown,
    renderedMarkdown,
    setRenderedMarkdown,
    renderEngine,
    setRenderEngine,
    switchRenderEngine,
    convertFormulas,
    loadMarkdownFromFile,
    convertAndRender
  };
};